import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import LedgerCard from "../components/LedgerCard";
import LedgerModal from "../components/LedgerModal";
import CustomSelectDropdown from "../components/CustomSelectDropdown";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Plus,
  Search,
  Book,
  Filter,
  Grid,
  List,
  Download,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ChevronDown,
  X,
  Calendar,
  ArrowUpDown,
} from "lucide-react";

const Ledgers = () => {
  const navigate = useNavigate();
  const [ledgers, setLedgers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingLedger, setEditingLedger] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [filters, setFilters] = useState({
    category: "all",
    status: "all",
    dateRange: "all",
    sortBy: "newest",
  });
  const [showFilters, setShowFilters] = useState(false);

  const [deleteLedger, setDeleteLedger] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchLedgers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/ledgers");

      if (response.data && response.data.success) {
        const ledgersData = response.data.data || [];
        setLedgers(Array.isArray(ledgersData) ? ledgersData : []);
      } else {
        setLedgers(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching ledgers:", error);
      setLedgers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLedgers();
  }, []);

  const handleCreateLedger = () => {
    setEditingLedger(null);
    setIsModalOpen(true);
  };

  const handleEditLedger = (ledger) => {
    setEditingLedger(ledger);
    setIsModalOpen(true);
  };

  const handleDeleteLedger = async () => {
    if (!deleteLedger) return;
    try {
      await api.delete(`/ledgers/${deleteLedger._id}`);
      fetchLedgers();
      setShowDeleteConfirm(false);
      setDeleteLedger(null);
    } catch (error) {
      console.error("Error deleting ledger:", error);
      setShowDeleteConfirm(false);
      setDeleteLedger(null);
    }
  };

  const handleCreateSuccess = () => {
    setIsModalOpen(false);
    setEditingLedger(null);
    fetchLedgers();
  };

  const handleLedgerClick = (ledger) => {
    navigate(`/ledgers/${ledger._id}`);
  };

  // Calculate totals
  const totalIncome = ledgers.reduce(
    (sum, ledger) => sum + (ledger.totalIncome || 0),
    0
  );
  const totalExpense = ledgers.reduce(
    (sum, ledger) => sum + (ledger.totalExpense || 0),
    0
  );
  const totalBalance = totalIncome - totalExpense;
  const totalTransactions = ledgers.reduce(
    (sum, ledger) => sum + (ledger.transactionCount || 0),
    0
  );

  // Filter and sort ledgers
  const filteredLedgers = ledgers
    .filter((ledger) => {
      if (!ledger || !ledger.name) return false;

      // Search filter
      if (
        searchQuery &&
        !ledger.name.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      // Category filter
      if (filters.category !== "all" && ledger.category !== filters.category) {
        return false;
      }

      // Status filter
      if (filters.status !== "all" && ledger.status !== filters.status) {
        return false;
      }

      // Date range filter (simplified)
      if (filters.dateRange !== "all") {
        const ledgerDate = new Date(ledger.createdAt);
        const now = new Date();
        const diffDays = Math.floor((now - ledgerDate) / (1000 * 60 * 60 * 24));

        switch (filters.dateRange) {
          case "today":
            if (diffDays > 1) return false;
            break;
          case "week":
            if (diffDays > 7) return false;
            break;
          case "month":
            if (diffDays > 30) return false;
            break;
        }
      }

      return true;
    })
    .sort((a, b) => {
      switch (filters.sortBy) {
        case "newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "oldest":
          return new Date(a.createdAt) - new Date(b.createdAt);
        case "name":
          return a.name.localeCompare(b.name);
        case "balance_high":
          return (b.balance || 0) - (a.balance || 0);
        case "balance_low":
          return (a.balance || 0) - (b.balance || 0);
        default:
          return 0;
      }
    });

  const categories = [
    "All",
    "Personal",
    "Business",
    "Household",
    "Travel",
    "Project",
  ];
  const statuses = ["All", "Active", "Archived", "Draft"];
  const dateRanges = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
  ];
  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "name", label: "A to Z" },
    { value: "balance_high", label: "Balance (High to Low)" },
    { value: "balance_low", label: "Balance (Low to High)" },
  ];

  const clearFilters = () => {
    setFilters({
      category: "all",
      status: "all",
      dateRange: "all",
      sortBy: "newest",
    });
    setSearchQuery("");
  };
  const handleExport = () => {
    if (filteredLedgers.length === 0) {
      alert("No data to export!");
      return;
    }

    // Prepare data for export
    const exportData = filteredLedgers.map((ledger) => ({
      "Book Name": ledger.name || "Unnamed Book",
      Description: ledger.description || "-",
      Category: ledger.category || "Uncategorized",
      Status: ledger.status || "Active",
      Balance: ledger.balance || 0,
      Income: ledger.totalIncome || 0,
      Expense: ledger.totalExpense || 0,
      "Transaction Count": ledger.transactionCount || 0,
      "Created Date": new Date(ledger.createdAt).toLocaleDateString("en-IN"),
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths (optional but looks better)
    worksheet["!cols"] = [
      { wch: 20 }, // Book Name
      { wch: 30 }, // Description
      { wch: 12 }, // Category
      { wch: 10 }, // Status
      { wch: 15 }, // Balance
      { wch: 15 }, // Income
      { wch: 15 }, // Expense
      { wch: 15 }, // Transaction Count
      { wch: 15 }, // Created Date
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Books");

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    const fileName = `Financial_Books_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    saveAs(data, fileName);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Books</h1>
          <p className="text-gray-500 mt-1">
            Manage and track all your expense books
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchLedgers()}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 text-sm"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={handleCreateLedger}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Plus size={18} />
            New Book
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Books</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {ledgers.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Book className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Income</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">
                ₹{totalIncome.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Expense</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">
                ₹{totalExpense.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-rose-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Balance</p>
              <p
                className={`text-2xl font-bold mt-1 ${
                  totalBalance >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                ₹{Math.abs(totalBalance).toLocaleString("en-IN")}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <span
                className={`text-lg font-bold ${
                  totalBalance >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                {totalBalance >= 0 ? "↑" : "↓"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-lg">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search books by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "grid"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <List size={16} />
              </button>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                showFilters
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Filter size={16} />
              Filters
              {Object.values(filters).some(
                (v) => v !== "all" && v !== "newest"
              ) && (
                <span className="bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {
                    Object.values(filters).filter(
                      (v) => v !== "all" && v !== "newest"
                    ).length
                  }
                </span>
              )}
            </button>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={isLoading || filteredLedgers.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isLoading || filteredLedgers.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Download size={16} />
              Export to Excel
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Filter Books</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <CustomSelectDropdown
                  value={
                    filters.category === "all"
                      ? "All"
                      : filters.category.charAt(0).toUpperCase() +
                        filters.category.slice(1)
                  }
                  options={categories}
                  onChange={(val) =>
                    setFilters({ ...filters, category: val.toLowerCase() })
                  }
                  placeholder="Select category"
                  leftIcon={Book}
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <CustomSelectDropdown
                  value={
                    filters.status === "all"
                      ? "All"
                      : filters.status.charAt(0).toUpperCase() +
                        filters.status.slice(1)
                  }
                  options={statuses}
                  onChange={(val) =>
                    setFilters({ ...filters, status: val.toLowerCase() })
                  }
                  placeholder="Select status"
                  leftIcon={Filter}
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <CustomSelectDropdown
                  value={
                    filters.dateRange === "all"
                      ? "All Time"
                      : dateRanges.find((r) => r.value === filters.dateRange)
                          ?.label || "All Time"
                  }
                  options={dateRanges.map((r) => r.label)}
                  onChange={(val) => {
                    const selected = dateRanges.find((r) => r.label === val);
                    setFilters({
                      ...filters,
                      dateRange: selected?.value || "all",
                    });
                  }}
                  placeholder="Select date range"
                  leftIcon={Calendar} // You can add Calendar from lucide-react if you want
                />
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <CustomSelectDropdown
                  value={
                    sortOptions.find((opt) => opt.value === filters.sortBy)
                      ?.label || "Newest First"
                  }
                  options={sortOptions.map((opt) => opt.label)}
                  onChange={(val) => {
                    const selected = sortOptions.find(
                      (opt) => opt.label === val
                    );
                    setFilters({
                      ...filters,
                      sortBy: selected?.value || "newest",
                    });
                  }}
                  placeholder="Sort by"
                  leftIcon={ArrowUpDown} // Optional: use ArrowUpDown icon
                />
              </div>
            </div>

            {/* Active Filters Tags */}
            {(filters.category !== "all" ||
              filters.status !== "all" ||
              filters.dateRange !== "all") && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-600">Active filters:</span>

                {filters.category !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full">
                    Category: {filters.category}
                    <button
                      onClick={() =>
                        setFilters({ ...filters, category: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}

                {filters.status !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                    Status: {filters.status}
                    <button
                      onClick={() => setFilters({ ...filters, status: "all" })}
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}

                {filters.dateRange !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                    {
                      dateRanges.find((r) => r.value === filters.dateRange)
                        ?.label
                    }
                    <button
                      onClick={() =>
                        setFilters({ ...filters, dateRange: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ledgers Grid/List */}
      {isLoading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading your books...</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              All Books ({filteredLedgers.length})
            </h2>
            <p className="text-sm text-gray-500">
              Showing {filteredLedgers.length} of {ledgers.length} books
            </p>
          </div>

          {filteredLedgers.length > 0 ? (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredLedgers.map((ledger) => (
                  <LedgerCard
                    key={ledger._id}
                    ledger={ledger}
                    onClick={() => handleLedgerClick(ledger)}
                    onEdit={handleEditLedger}
                    onDelete={(ledger) => {
                      setDeleteLedger(ledger);
                      setShowDeleteConfirm(true);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLedgers.map((ledger) => (
                  <div
                    key={ledger._id}
                    className="bg-white p-4 rounded-xl border border-gray-200 hover:border-indigo-300 transition-colors cursor-pointer"
                    onClick={() => handleLedgerClick(ledger)}
                  >
                    {/* List view content */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Book size={18} className="text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {ledger.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {ledger.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Balance</p>
                          <p
                            className={`font-semibold ${
                              ledger.balance >= 0
                                ? "text-emerald-600"
                                : "text-rose-600"
                            }`}
                          >
                            ₹{(ledger.balance || 0).toLocaleString("en-IN")}
                          </p>
                        </div>
                        <ChevronDown size={18} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Book className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No books found
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {searchQuery ||
                Object.values(filters).some(
                  (v) => v !== "all" && v !== "newest"
                )
                  ? "No books match your current search and filters. Try adjusting your criteria."
                  : "Create your first book to start tracking your finances."}
              </p>
              <div className="flex gap-3 justify-center">
                {searchQuery ||
                Object.values(filters).some(
                  (v) => v !== "all" && v !== "newest"
                ) ? (
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
                  >
                    Clear Filters
                  </button>
                ) : null}
                <button
                  onClick={handleCreateLedger}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
                >
                  <Plus size={18} className="inline mr-2" />
                  Create Your First Book
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-6 rounded-xl max-w-sm w-full shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the book{" "}
              <span className="font-medium">{deleteLedger?.name}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteLedger(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLedger}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      <LedgerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingLedger(null);
        }}
        onSuccess={handleCreateSuccess}
        editingLedger={editingLedger}
      />
    </div>
  );
};

export default Ledgers;
